import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Sparkles, Library as LibraryIcon, Users, Cpu, DollarSign, Globe, Lock, Download, Video, ImageIcon, RefreshCw, Maximize2, Brush, Film, Wand2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import VideoThumbnail from '../components/VideoThumbnail';
import { formatCredits } from '../utils/credits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: 'generate', label: 'Generate', icon: Sparkles, path: '/generate' },
  { id: 'canvas', label: 'Canvas', icon: Brush, path: '/canvas' },
  { id: 'image-to-video', label: 'Img → Vid', icon: Film, path: '/image-to-video' },
  { id: 'library', label: 'Library', icon: LibraryIcon, path: '/library' },
  { id: 'community', label: 'Community', icon: Users, path: '/community' },
];

export default function MyLibrary() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchLibrary = useCallback(async (kind) => {
    setLoading(true);
    try {
      const url = kind && kind !== 'all' ? `${API}/library?kind=${kind}&limit=120` : `${API}/library?limit=120`;
      const res = await axios.get(url, { withCredentials: true });
      setItems(res.data || []);
    } catch (err) { toast.error('Could not load library'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLibrary(filter); }, [filter, fetchLibrary]);

  const downloadImage = (path) => {
    const a = document.createElement('a');
    a.href = `${API}/files/${path}`;
    a.download = path.split('/').pop();
    document.body.appendChild(a); a.click(); a.remove();
  };

  const togglePublic = async (jobId, currentPublic) => {
    try {
      await axios.patch(`${API}/library/${jobId}/publish`, { is_public: !currentPublic }, { withCredentials: true });
      toast.success(!currentPublic ? 'Published 🎨' : 'Made private');
      fetchLibrary(filter);
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex" data-testid="library-page">
      <Sidebar location={location} user={user} />
      <main className="flex-1 overflow-y-auto">
        <header className="px-6 lg:px-10 py-8 border-b border-white/[0.05]">
          <h1 className="font-display text-3xl font-bold text-white mb-1" data-testid="library-heading">My Library</h1>
          <p className="text-sm text-white/50">Everything you&apos;ve created on DIS2ORT.</p>
          <div className="flex gap-1.5 mt-5">
            {['all', 'image', 'video'].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${filter === f ? 'bg-[#FF6B4A] text-white' : 'bg-white/[0.05] text-white/60 hover:bg-white/[0.1]'}`}>
                {f}
              </button>
            ))}
          </div>
        </header>
        <div className="p-6 lg:p-10">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {Array.from({ length: 10 }).map((_, i) => <div key={`skel-${i}`} className="aspect-square rounded-lg bg-white/[0.03] animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyLibrary />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="library-grid">
              {items.map((job) => (
                <LibraryCard key={job.job_id} job={job} onDownload={downloadImage} onTogglePublic={togglePublic}
                  onRemix={(id) => navigate(`/generate?remix=${id}`)}
                  onAnimate={(path) => navigate(`/image-to-video?source=${encodeURIComponent(path)}`)}
                  onUpscale={async (id, factor) => {
                    try {
                      toast.loading(`Upscaling ${factor}×…`, { id: `up-${id}` });
                      await axios.post(`${API}/upscale`, { job_id: id, factor }, { withCredentials: true });
                      toast.success(`Upscaled ${factor}× ✨`, { id: `up-${id}` });
                      fetchLibrary(filter);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed', { id: `up-${id}` });
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Sidebar({ location, user }) {
  return (
    <aside className="w-[68px] lg:w-[200px] border-r border-white/[0.05] bg-[#08080C] flex flex-col">
      <Link to="/" className="h-16 flex items-center justify-center lg:justify-start lg:px-5 border-b border-white/[0.05]">
        <span className="font-body text-xl font-extrabold tracking-tight text-white">D<span className="text-[#FF6B4A]">2</span><span className="hidden lg:inline">ORT</span></span>
      </Link>
      <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
        {NAV_ITEMS.map((it) => {
          const active = location.pathname === it.path;
          return (
            <Link key={it.id} to={it.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-[#FF6B4A]/15 text-[#FF6B4A]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'}`}>
              <it.icon className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">{it.label}</span>
            </Link>
          );
        })}
        <div className="pt-4 mt-4 border-t border-white/[0.05] space-y-1">
          <Link to="/models" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-[#FFD93D] hover:bg-[#FFD93D]/[0.06]">
            <Cpu className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">150 Models</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <DollarSign className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Pricing</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <Sparkles className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Credits</span>
          </Link>
        </div>
      </nav>
      {user && (
        <div className="hidden lg:flex items-center gap-2 px-3 pb-4 text-white/60">
          {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[#FF6B4A]/30" />}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-white truncate">{user.name?.split(' ')[0]}</div>
            <div className="text-[9px] text-white/50">{formatCredits(user)} cr</div>
          </div>
        </div>
      )}
    </aside>
  );
}

function LibraryCard({ job, onDownload, onTogglePublic, onRemix, onUpscale, onAnimate }) {
  const [hover, setHover] = useState(false);
  const cardRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rX = useSpring(useTransform(my, [0, 1], [4, -4]), { damping: 26, stiffness: 200 });
  const rY = useSpring(useTransform(mx, [0, 1], [-6, 6]), { damping: 26, stiffness: 200 });
  const handleMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const handleLeave = () => { mx.set(0.5); my.set(0.5); setHover(false); };
  const images = job.image_paths && job.image_paths.length > 0 ? job.image_paths : (job.output_path ? [job.output_path] : []);
  if (images.length === 0) return null;
  const isVideo = job.service_type === 'ai_video' || job.service_type === 'video_render';
  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={handleLeave}
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{ rotateX: rX, rotateY: rY, transformStyle: 'preserve-3d', transformPerspective: 1200 }}
      className="group relative rounded-lg overflow-hidden bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.18] aspect-square">
      <motion.div animate={{ scale: hover ? 1.05 : 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0">
        {isVideo ? (
          <VideoThumbnail path={images[0]} title={job.title} className="w-full h-full" />
        ) : (
          <img src={`${API}/files/${images[0]}`} alt={job.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </motion.div>
      {/* Sheen sweep */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.18) 50%, transparent 62%)',
          transform: hover ? 'translateX(110%)' : 'translateX(-110%)',
          transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
      {hover && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-2.5">
          <div className="text-[10px] text-white/80 line-clamp-2 mb-2">{job.title}</div>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => onRemix(job.job_id)} title="Remix" data-testid={`lib-remix-${job.job_id}`}
              className="w-7 h-7 rounded-md bg-[#FF6B4A]/30 hover:bg-[#FF6B4A]/50 flex items-center justify-center transition-transform hover:scale-110">
              <RefreshCw className="w-3.5 h-3.5 text-[#FF6B4A]" />
            </button>
            {!isVideo && onAnimate && (
              <button onClick={() => onAnimate(images[0])} title="Animate this" data-testid={`lib-animate-${job.job_id}`}
                className="w-7 h-7 rounded-md bg-[#E040A0]/30 hover:bg-[#E040A0]/50 flex items-center justify-center transition-transform hover:scale-110">
                <Wand2 className="w-3.5 h-3.5 text-[#E040A0]" />
              </button>
            )}
            {!isVideo && onUpscale && (
              <button onClick={() => onUpscale(job.job_id, 2)} title="Upscale 2×" data-testid={`lib-upscale-${job.job_id}`}
                className="w-7 h-7 rounded-md bg-[#A855F7]/30 hover:bg-[#A855F7]/50 flex items-center justify-center transition-transform hover:scale-110">
                <Maximize2 className="w-3.5 h-3.5 text-[#A855F7]" />
              </button>
            )}
            <button onClick={() => onDownload(images[0])} className="w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center transition-transform hover:scale-110"><Download className="w-3.5 h-3.5 text-white" /></button>
            <button onClick={() => onTogglePublic(job.job_id, job.is_public)}
              className={`w-7 h-7 rounded-md flex items-center justify-center transition-transform hover:scale-110 ${job.is_public ? 'bg-[#34B76F]/30 hover:bg-[#34B76F]/40' : 'bg-white/10 hover:bg-white/20'}`}>
              {job.is_public ? <Globe className="w-3.5 h-3.5 text-[#34B76F]" /> : <Lock className="w-3.5 h-3.5 text-white" />}
            </button>
          </div>
        </div>
      )}
      {job.is_public && <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#34B76F]/80 flex items-center justify-center"><Globe className="w-2.5 h-2.5 text-white" /></div>}
    </motion.div>
  );
}

function EmptyLibrary() {
  return (
    <div className="text-center py-20" data-testid="empty-library">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6B4A]/20 to-[#E040A0]/20 flex items-center justify-center mx-auto mb-4">
        <ImageIcon className="w-7 h-7 text-[#FF6B4A]" />
      </div>
      <h2 className="font-display text-xl font-bold text-white mb-2">Your library is empty</h2>
      <p className="text-sm text-white/50 mb-6">Create your first generation to see it here.</p>
      <Link to="/generate" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#FF6B4A] text-white text-xs font-semibold hover:bg-[#FF8E72]">
        <Sparkles className="w-3.5 h-3.5" /> Generate now
      </Link>
    </div>
  );
}
